import { Controller, useFieldArray, useWatch, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { Binoculars, Copy, Plus, Trash2 } from 'lucide-react';
import { Button, Checkbox, DateField, EmptyState, FormField, IconButton, NumberField, TextField } from '@/components';
import { useCodeValues } from '@/features/masters';
import { emptySighting, type GearRow, type SightingRow } from '../sightings';
import '../voyage-reports.css';
import { ControlledSelect } from '@/features/advance-notices/components/ControlledSelect';

/** 視認・漁具の明細を持つフォームの共通部分 */
export interface SightingsFormPart {
  sightings: SightingRow[];
  gearFindings: GearRow[];
}

const opts = (list: readonly string[] | undefined) => (list ?? []).map((v) => ({ value: v, label: v }));

/** 度・分・秒（第3欄 最大4桁）の入力 */
export function DmsInput({ register, base, label }: { register: UseFormRegister<SightingsFormPart>; base: string; label: '緯度' | '経度' }) {
  const p = base as `sightings.${number}`;
  const k = label === '緯度' ? 'lat' : 'lon';
  return (
    <span className="bs-dms">
      <span className="bs-dms__hemi">{label === '緯度' ? 'N' : 'E'}</span>
      <TextField width="xs" aria-label={`${label}（度）`} {...register(`${p}.${k}Deg` as `sightings.${number}.latDeg`)} />
      <span>°</span>
      <TextField width="xs" aria-label={`${label}（分）`} {...register(`${p}.${k}Min` as `sightings.${number}.latMin`)} />
      <span>′</span>
      <TextField width="xs" aria-label={`${label}（秒）`} {...register(`${p}.${k}Sec` as `sightings.${number}.latSec`)} />
    </span>
  );
}

/**
 * 視認状況報告の明細入力（定時連絡・速報で共用。03_作業分担 §3.2）。
 * 行は sighting_reports の 1 行に対応し、保存時に船×期間の行と同期される。
 */
export function SightingTable({
  control,
  register,
  defaultDate,
  refSource,
  errors,
  readOnly,
}: {
  control: Control<SightingsFormPart>;
  register: UseFormRegister<SightingsFormPart>;
  defaultDate: string;
  refSource: 'fixed' | 'voyage';
  errors?: FieldErrors<SightingsFormPart>['sightings'];
  readOnly?: boolean;
}) {
  const { data: codes } = useCodeValues();
  const rows = useFieldArray({ control, name: 'sightings' });
  const values = useWatch({ control, name: 'sightings' });
  return (
    <div className="bs-detail-rows">
      {rows.fields.length === 0 && <EmptyState compact icon={<Binoculars />} title="視認状況報告はありません" description="外国漁船を視認した場合は「視認情報を追加」から入力してください。" />}
      {rows.fields.map((f, i) => {
        const p = `sightings.${i}` as const;
        const e = (errors?.[i] ?? {}) as Record<string, { message?: string } | undefined>;
        const v = values?.[i];
        return (
          <section key={f.id} className="bs-detail-row" aria-label={`視認 ${i + 1}件目`}>
            <header className="bs-detail-row__head">
              <span className="bs-detail-row__no">視認 {String(i + 1).padStart(2, '0')}</span>
              {v?.zoneNoCalculated != null && <span className="bs-detail-row__calc">機械算出 農林漁区番号：{v.zoneNoCalculated}</span>}
              {!readOnly && (
                <span className="bs-detail-row__tools">
                  <IconButton label="行をコピー" icon={<Copy size={15} />} onClick={() => rows.insert(i + 1, { ...(v ?? emptySighting(defaultDate, refSource)), id: null, zoneNoCalculated: null })} />
                  <IconButton label="行を削除" variant="danger" icon={<Trash2 size={15} />} onClick={() => rows.remove(i)} />
                </span>
              )}
            </header>
            <fieldset className="bs-detail-row__body" disabled={readOnly}>
              <FormField label="視認日" required error={e.sightedOn?.message} className="is-w2">
                <DateField {...register(`${p}.sightedOn`)} />
              </FormField>
              <FormField label="時刻" className="is-w2" hint="24時間 HH:MM">
                <TextField className="bs-num" placeholder="12:00" {...register(`${p}.sightedTime`)} />
              </FormField>
              <FormField label="監視作業水域（通称漁場名）" className="is-w2">
                <TextField {...register(`${p}.monitoringArea`)} />
              </FormField>
              <FormField label="農林漁区番号" className="is-w2" error={e.zoneNo?.message}>
                <Controller control={control} name={`${p}.zoneNo`} render={({ field }) => <NumberField {...field} width="full" aria-label="農林漁区番号" />} />
              </FormField>
              <FormField label="緯度・経度" className="is-w4" hint="度・分・秒（第3欄は最大4桁）">
                <span className="bs-dms-pair">
                  <DmsInput register={register} base={p} label="緯度" />
                  <DmsInput register={register} base={p} label="経度" />
                </span>
              </FormField>
              <FormField label="操業・航海・漂泊" className="is-w2">
                <ControlledSelect control={control} name={`${p}.activity`} placeholder="—" options={opts(codes?.vesselActivities)} />
              </FormField>
              <FormField label="進行方向等" className="is-w1">
                <TextField {...register(`${p}.heading`)} />
              </FormField>
              <FormField label="国籍" className="is-w2">
                <ControlledSelect control={control} name={`${p}.nationality`} placeholder="—" options={opts(codes?.nationalities)} />
              </FormField>
              <FormField label="船名" className="is-w3">
                <TextField {...register(`${p}.foreignVesselName`)} />
              </FormField>
              <FormField label="登録番号" className="is-w2">
                <TextField {...register(`${p}.foreignRegistrationNo`)} />
              </FormField>
              <FormField label="承認番号" className="is-w2">
                <TextField {...register(`${p}.approvalNo`)} />
              </FormField>
              <FormField label="漁業種類" className="is-w2">
                <ControlledSelect control={control} name={`${p}.fishingType`} placeholder="—" options={opts(codes?.foreignFishingTypes)} />
              </FormField>
              <FormField label="隻数" required className="is-w2" error={e.vesselCount?.message}>
                <Controller control={control} name={`${p}.vesselCount`} render={({ field }) => <NumberField {...field} unit="隻" width="full" aria-label="隻数" />} />
              </FormField>
              <FormField label="通報・写真・参照資料" className="is-w3">
                <span className="bs-checks">
                  <Controller control={control} name={`${p}.isReported`} render={({ field }) => <Checkbox label="通報あり" checked={field.value} onChange={(x) => field.onChange(x.target.checked)} />} />
                  <Controller control={control} name={`${p}.hasPhoto`} render={({ field }) => <Checkbox label="写真あり" checked={field.value} onChange={(x) => field.onChange(x.target.checked)} />} />
                  <Controller control={control} name={`${p}.refFixedTime`} render={({ field }) => <Checkbox label="定時" checked={field.value} onChange={(x) => field.onChange(x.target.checked)} />} />
                  <Controller control={control} name={`${p}.refVoyage`} render={({ field }) => <Checkbox label="速報" checked={field.value} onChange={(x) => field.onChange(x.target.checked)} />} />
                </span>
              </FormField>
              <FormField label="特記事項" className="is-w5">
                <TextField {...register(`${p}.specialNotes`)} />
              </FormField>
              <FormField label="備考" className="is-w6">
                <TextField {...register(`${p}.note`)} />
              </FormField>
            </fieldset>
          </section>
        );
      })}
      {!readOnly && (
        <div>
          <Button size="sm" icon={<Plus size={15} />} onClick={() => rows.append(emptySighting(defaultDate, refSource))}>
            視認情報を追加
          </Button>
        </div>
      )}
    </div>
  );
}
